import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  FolderPlus,
  Images,
  Plus,
  Radio,
  Upload,
  Users,
  AlertCircle,
} from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";
import Text from "../../../../components/typography/Text";

import Button from "../../../../components/buttons/Button";
import EmptyState from "../../../../components/feedback/EmptyState";
import FormModal from "../../../../components/modals/FormModal";
import PrimaryPillTabs from "../../../../components/tabs/PrimaryPillTabs";

import GroupsCreatorView from "../../../groups/creators/views/groupLists/GroupsCreatorView";
import CreatorAlbumsView from "../../../albums/creators/views/CreatorAlbumsView";
import CreatorPopsView from "../../../pops/creators/views/CreatorPopsView";
import CreatorVideosView from "../../../videos/creators/views/CreatorVideosView";
import CreatorStreamsView from "../../../streams/creators/views/CreatorStreamsView";

import CreatorAccessPage from "../../access/pages/CreatorAccessPage";

import CreatorCreateStreamForm from "../../../streams/creators/views/forms/CreatorCreateStreamForm";
import CreatorCreateGroupForm from "../../../groups/creators/forms/CreatorCreateGroupForm";
import CreatorCreateAlbumForm from "../../../albums/creators/forms/CreatorCreateAlbumForm";
import CreatorVideoUploadForm from "../../../videos/creators/forms/uploads/CreatorVideoUploadForm";
import CreatorPopUploadForm from "../../../pops/creators/forms/CreatorPopUploadForm";

import DropdownMenu from "../../../../components/menu/dropdown/DropdownMenu";

import { useCreators } from "../hooks/useCreators";

import "./CreatorsView.css";

const CREATE_OPTIONS = [
  { id: "video", label: "Upload Video", icon: Upload },
  { id: "pop", label: "Upload Pop", icon: Plus },
  { id: "album", label: "Create Album", icon: Images },
  { id: "group", label: "Create Group", icon: Users },
  { id: "stream", label: "Create Stream", icon: Radio },
];

export default function CreatorView({ userId }) {
  const { username, media } = useParams();
  const navigate = useNavigate();

  const { creator, loading, error, getCreator } = useCreators(userId);

  const [activeModal, setActiveModal] = useState(null);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [popUploading, setPopUploading] = useState(false);

  const createMenuRef = useRef(null);

  const currentActiveId = media || "videos";

  const tabConfig = [
    {
      id: "videos",
      label: "Videos",
      path: `/creators/${username}/videos`,
    },
    {
      id: "pops",
      label: "Pops",
      path: `/creators/${username}/pops`,
    },
    {
      id: "albums",
      label: "Albums",
      path: `/creators/${username}/albums`,
    },
    {
      id: "more",
      label: "More",
      menu: [
        {
          id: "groups",
          label: "Groups",
          path: `/creators/${username}/groups`,
        },
        {
          id: "streams",
          label: "Streams",
          path: `/creators/${username}/streams`,
        },
        {
          id: "access",
          label: "Access",
          path: `/creators/${username}/access`,
        },
      ],
    },
  ];

  useEffect(() => {
    if (!userId || !username) return;

    void getCreator(username);
  }, [userId, username]);

  useEffect(() => {
    setShowCreateMenu(false);
  }, [username, media]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        createMenuRef.current &&
        !createMenuRef.current.contains(event.target)
      ) {
        setShowCreateMenu(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowCreateMenu(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleTabChange = (tab) => {
    if (!tab?.path) return;

    navigate(tab.path);
  };

  const handleOpenModal = (modal) => {
    setShowCreateMenu(false);
    setActiveModal(modal);
  };

  const handleCloseModal = () => {
    if (
      (activeModal === "video" && videoUploading) ||
      (activeModal === "pop" && popUploading)
    ) {
      return;
    }

    setActiveModal(null);
  };

  const handleSuccess = (tab) => {
    setActiveModal(null);
    setVideoUploading(false);
    setPopUploading(false);

    navigate(`/creators/${creator.username}/${tab}`);
    navigate(0);
  };

  const renderCreateMenu = () => (
    <DropdownMenu
      label="Create content"
      align="end"
      width={210}
      items={CREATE_OPTIONS.map((option) => ({
        id: option.id,
        label: option.label,
        icon: option.icon,
        onClick: () => handleOpenModal(option.id),
      }))}
      trigger={
        <Button
          value="Create"
          icon={<FolderPlus size={17} />}
          variant="primary"
        />
      }
    />
  );

  const renderContent = () => {
    switch (currentActiveId) {
      case "videos":
        return <CreatorVideosView userId={userId} username={username} />;

      case "pops":
        return <CreatorPopsView userId={userId} username={username} />;

      case "albums":
        return <CreatorAlbumsView userId={userId} username={username} />;

      case "groups":
        return <GroupsCreatorView userId={userId} username={username} />;

      case "streams":
        return <CreatorStreamsView userId={userId} username={username} />;

      case "access":
        return <CreatorAccessPage userId={userId} username={username} />;

      default:
        return (
          <EmptyState
            icon={<AlertCircle size={28} />}
            title="Section unavailable"
            description="This creator section could not be found."
          />
        );
    }
  };

  const renderModalForm = () => {
    if (!creator) return null;

    const commonProps = {
      username: creator.username,
      onClose: handleCloseModal,
    };

    switch (activeModal) {
      case "video":
        return (
          <CreatorVideoUploadForm
            {...commonProps}
            onUploadingChange={setVideoUploading}
            onSuccess={() => handleSuccess("videos")}
          />
        );

      case "pop":
        return (
          <CreatorPopUploadForm
            {...commonProps}
            onUploadingChange={setPopUploading}
            onSuccess={() => handleSuccess("pops")}
          />
        );

      case "album":
        return (
          <CreatorCreateAlbumForm
            {...commonProps}
            onSuccess={() => handleSuccess("albums")}
          />
        );

      case "group":
        return (
          <CreatorCreateGroupForm
            {...commonProps}
            onSuccess={() => handleSuccess("groups")}
          />
        );

      case "stream":
        return (
          <CreatorCreateStreamForm
            {...commonProps}
            onSuccess={() => handleSuccess("streams")}
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <ViewContainer>
        <div className="creator-studio__loading" role="status">
          <Text as="p" tone="secondary" size="sm">
            Loading creator...
          </Text>
        </div>
      </ViewContainer>
    );
  }

  if (error || !creator) {
    return (
      <ViewContainer>
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={error ? "Unable to load creator" : "Creator not found"}
          description={
            error
              ? typeof error === "string"
                ? error
                : error.message || "Unable to load creator."
              : "This creator profile is unavailable."
          }
        />
      </ViewContainer>
    );
  }

  return (
    <ViewContainer>
      <ViewHeader
        title={creator.brandName || creator.username || "Creator Studio"}
        description={`@${creator.username}`}
        actions={renderCreateMenu()}
        size={"xl"}
      />

      <PrimaryPillTabs
        tabs={tabConfig}
        activeTab={currentActiveId}
        onChange={handleTabChange}
      />

      <div className="creator-studio__content">{renderContent()}</div>

      <FormModal
        isOpen={Boolean(activeModal)}
        onClose={handleCloseModal}
        closeDisabled={
          (activeModal === "video" && videoUploading) ||
          (activeModal === "pop" && popUploading)
        }
      >
        {activeModal && renderModalForm()}
      </FormModal>
    </ViewContainer>
  );
}
